/* This FaqTemplate requires Tailwind CSS v2.0+ */
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import MarkdownBlock from './Chat/MarkdownBlock';

const faqs = [
  {
    question: 'Can I create a custom copy of my own voice?',
    answer: `Yes, you can! We offer a custom voice cloning service. Once you have created the account, go to AI Text to Speech Generator, then click on “Clone your voice” to begin the process.

Next, you’ll need to upload or record a sample recording of your voice. It’s important to note that the sample should only contain one speaker and be at least 1 minute long. Also, make sure there is no background noise in the recording as it can affect the accuracy of the voice cloning. Once you have your sample recording ready, our AI will analyze your unique voice pattern and replicate it. After the analysis is complete, you’ll have your very own digital voice clone, ready to generate audio in your own voice.`,
  },
  {
    question: 'Do I own the outputted audio files?',
    answer:
      'Yes, like with all outputs from our AI, the person who created them owns the output. You can use the generated audio files for any purpose, including commercial use. Provided you have the necessary intellectual property rights to the content you are generating and the content does not break any laws.',
  },
  {
    question: 'How can I control the emotional range of the generated audio?',
    answer: `Our AI model looks at the whole text to understand the emotion it should use. Here are some tips to help it understand the emotion you want:
- The text you input should match the emotion you want. For example, if you want the AI to sound happy, input a happy text.
- Punctuation and voice settings are important. They help the AI understand how to say the text.
- If you want to emphasize certain words or phrases, put them in quotation marks.
- If you're using a cloned voice, the AI will copy the style of the sample you uploaded. So, if the sample is monotone, the AI will also sound monotone.
- These tips can help, but they don't guarantee the result. We're working on features that will let you control the emotion in the text more directly.`,
  },
  {
    question:
      'What AI voices are popular on TikTok, YouTube, and social media?',
    answer: `You may have come across AI voices on social media platforms like TikTok, YouTube, Twitter, Instagram, etc. These voices are often generated using our service. Some of the most commonly used voices on these platforms include:


#### Male Voices:

- Adam
- Antoni


#### Female Voices:

- Rachel
- Matilda

These are just a few examples. We offer a wide range of voices. To explore more and find the perfect voice for your needs, we recommend signing up for a free account.`,
  },
];

export default function TTSFaq() {
  const { t } = useLocale('common');

  return (
    <div className='bg-gray-50 dark:bg-slate-900'>
      <div className='mx-auto max-w-7xl py-12 px-4 sm:py-16 sm:px-6 lg:px-8'>
        <div
          className='mx-auto max-w-3xl divide-y-2 divide-gray-200 dark:divide-slate-600'
          itemScope
          itemType='https://schema.org/FAQPage'
        >
          <h2 className='text-center text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl dark:text-slate-200'>
            {t('Frequently asked questions')}
          </h2>
          <dl className='mt-6 space-y-6 divide-y divide-gray-200 dark:divide-slate-600'>
            {faqs.map((faq) => (
              <Disclosure
                as='div'
                key={faq.question}
                className='pt-6'
                itemScope
                itemProp='mainEntity'
                itemType='https://schema.org/Question'
              >
                {({ open }) => (
                  <>
                    <dt className='text-lg'>
                      <Disclosure.Button className='flex w-full items-start justify-between text-left text-gray-400 dark:text-slate-400'>
                        <span
                          className='font-medium text-gray-900 dark:text-slate-200'
                          itemProp='name'
                        >
                          {faq.question}
                        </span>
                        <span className='ml-6 flex h-7 items-center'>
                          <ChevronDownIcon
                            className={clsxm(
                              open ? '-rotate-180' : 'rotate-0',
                              'h-6 w-6 transform'
                            )}
                            aria-hidden='true'
                          />
                        </span>
                      </Disclosure.Button>
                    </dt>
                    <Disclosure.Panel
                      as='dd'
                      className='mt-2 pr-12'
                      itemScope
                      itemProp='acceptedAnswer'
                      unmount={false}
                      itemType='https://schema.org/Answer'
                    >
                      <div
                        className='text-base text-gray-500 dark:text-slate-400'
                        itemProp='text'
                      >
                        <MarkdownBlock
                          message={faq.answer.replace(/\n/g, '  \n')}
                        />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}
